import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ShopContext = createContext(null);

export function ShopProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0, count: 0 });
  const [wishlist, setWishlist] = useState([]);

  const isAuthed = user && user.user_id;

  const refreshCart = useCallback(async () => {
    if (!isAuthed) { setCart({ items: [], total: 0, count: 0 }); return; }
    try { const { data } = await api.get("/cart"); setCart(data); } catch {}
  }, [isAuthed]);

  const refreshWishlist = useCallback(async () => {
    if (!isAuthed) { setWishlist([]); return; }
    try { const { data } = await api.get("/wishlist"); setWishlist(data); } catch {}
  }, [isAuthed]);

  useEffect(() => { refreshCart(); refreshWishlist(); }, [refreshCart, refreshWishlist]);

  const addToCart = async (product_id, quantity = 1) => {
    if (!isAuthed) { toast.error("Please sign in to add items to your cart"); return false; }
    const { data } = await api.post("/cart", { product_id, quantity });
    setCart(data);
    toast.success("Added to cart");
    return true;
  };
  const updateCart = async (product_id, quantity) => {
    const { data } = await api.put("/cart", { product_id, quantity });
    setCart(data);
  };
  const removeFromCart = async (product_id) => {
    const { data } = await api.delete(`/cart/${product_id}`);
    setCart(data);
  };
  const toggleWishlist = async (product) => {
    if (!isAuthed) { toast.error("Please sign in to save items"); return; }
    const exists = wishlist.some((p) => p.id === product.id);
    if (exists) {
      await api.delete(`/wishlist/${product.id}`);
      toast("Removed from wishlist");
    } else {
      await api.post("/wishlist", { product_id: product.id });
      toast.success("Saved to wishlist");
    }
    refreshWishlist();
  };
  const inWishlist = (id) => wishlist.some((p) => p.id === id);

  return (
    <ShopContext.Provider value={{ cart, wishlist, addToCart, updateCart, removeFromCart, toggleWishlist, inWishlist, refreshCart, refreshWishlist }}>
      {children}
    </ShopContext.Provider>
  );
}

export const useShop = () => useContext(ShopContext);
